import React from "react";
import { Switch, Route, Redirect, useLocation } from "react-router";
import App from "../containers/App";
import ProtectedRoute from "../components/app/ProtectedRoute";

import TemplateResolver from "../components/templates/TemplateResolver";
import Lobby from "../containers/Lobby";
import MatchPage from "../containers/Match";
import MatchScoreboardPage from "../containers/MatchScoreboard";
import Account from "../containers/Account";
import Profile from "../containers/Profile";
import Leaderboard from "../containers/Leaderboard";
import Players from "../containers/Players";
import Reports from "../containers/Reports";
import ReportDetail from "../containers/ReportDetail";
import ReputationExplainer from "../containers/ReputationExplainer";
import ReputationDashboard from "../containers/ReputationDashboard";
import ReputationReview from "../containers/ReputationReview";
import TacticalProfile from "../containers/TacticalProfile";
import PlansPage from "../components/membership/PlansPage";
import MembershipSuccessPage from "../components/membership/MembershipSuccessPage";
import MembershipCancelPage from "../components/membership/MembershipCancelPage";

// import ProductPage from "../containers/ProductPage";
// import CartPage from "../containers/CartPage";
// import CheckoutPage from "../containers/CheckoutPage";
import PrivacyPolicyPage from "../containers/PrivacyPolicyPage";
import TermsOfUsePage from "../containers/TermsOfUsePage";
import AboutUsPage from "../containers/AboutUsPage";
import GDPRPage from "../containers/GdprPage";
import NotFound from "../containers/NotFound";
import HowItWorksPage from "../components/public-site/HowItWorksPage";
import TrustAndSafetyPage from "../components/public-site/TrustAndSafetyPage";
import FaqPage from "../components/public-site/FaqPage";
import ContactPage from "../components/public-site/ContactPage";
import PricingPage from "../components/public-site/PricingPage";
import SupportPage from "../components/public-site/SupportPage";
import AntiCheatPage from "../components/public-site/AntiCheatPage";
import KycCallbackPage from "../components/kyc/KycCallbackPage";
import GuardConnectPage from "../components/guard/GuardConnectPage";
import TemplatePageResolver from "../components/templates/TemplatePageResolver";
import PlaySafeStatusPage from "../components/auth/PlaySafeStatusPage";
import PlaySafeConnectSteamPage from "../components/auth/PlaySafeConnectSteamPage.jsx";
import MatchNightPage from "../components/match-night/MatchNightPage";
import LatestMatches from "../containers/LatestMatches";
import { toSettingsSectionPath } from "../components/account/accountSettingsNav";
import { isV2Path } from "./v2Routes";

// import { getHomeData } from "../redux/actions/home";
// import { getProductPageData } from "../redux/actions/product-page";
// import { getListPageData } from "../redux/actions/tire-results-page";
// import { getListPageDataForCategory } from "../redux/actions/category-results-page";
// import { getListByBrandPageData } from "../redux/actions/results-by-brand-page";

function createTemplatePage(pageKey, fallbackComponent) {
  return function TemplateRoutePage() {
    return (
      <TemplatePageResolver
        pageKey={pageKey}
        fallbackComponent={fallbackComponent}
      />
    );
  };
}

const TemplateAntiCheatPage = createTemplatePage("antiCheat", AntiCheatPage);
const TemplateAboutUsPage = createTemplatePage("about", AboutUsPage);
const TemplateContactPage = createTemplatePage("contact", ContactPage);
const TemplateFaqPage = createTemplatePage("faq", FaqPage);
const TemplateHowItWorksPage = createTemplatePage("howItWorks", HowItWorksPage);
const TemplatePricingPage = createTemplatePage("pricing", PricingPage);
const TemplateReputationPage = createTemplatePage("reputation", ReputationExplainer);
const TemplateSupportPage = createTemplatePage("support", SupportPage);
const TemplateTrustAndSafetyPage = createTemplatePage("trustAndSafety", TrustAndSafetyPage);
const TemplatePrivacyPolicyPage = createTemplatePage("privacy", PrivacyPolicyPage);
const TemplateTermsOfUsePage = createTemplatePage("termsOfUse", TermsOfUsePage);
const TemplateGDPRPage = createTemplatePage("gdpr", GDPRPage);

function LegacySettingsV2Redirect({ match, location }) {
  const fromSearch = new URLSearchParams(location.search).get("section");
  return (
    <Redirect
      to={toSettingsSectionPath(match?.params?.section || fromSearch)}
    />
  );
}

function LegacySupportTicketsRedirect({ match }) {
  const ticketId = match?.params?.ticketId;
  const supportPath = toSettingsSectionPath("support-tickets");
  return (
    <Redirect
      to={
        ticketId
          ? `${supportPath}#${encodeURIComponent(ticketId)}`
          : supportPath
      }
    />
  );
}

export const routes = [
  {
    path: "/",
    exact: true,
    component: TemplateResolver,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/match/:id",
    exact: true,
    component: MatchPage,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/match/:id/scoreboard",
    exact: true,
    component: MatchScoreboardPage,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/profile/:id",
    exact: true,
    component: Profile,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/lobby",
    exact: true,
    component: Lobby,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/matches",
    exact: true,
    component: LatestMatches,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/account",
    exact: true,
    component: Account,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/tactical-profile",
    exact: true,
    component: TacticalProfile,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/account/tickets",
    exact: true,
    component: LegacySupportTicketsRedirect,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/account/tickets/:ticketId",
    exact: true,
    component: LegacySupportTicketsRedirect,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/account/kyc/callback",
    exact: true,
    component: KycCallbackPage,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/account/reputation",
    exact: true,
    component: ReputationDashboard,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/leaderboard",
    exact: true,
    component: Leaderboard,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/players",
    exact: true,
    component: Players,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/lobby-v2",
    exact: true,
    component: Lobby,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/profile-v2/:id",
    exact: true,
    component: Profile,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/leaderboard-v2",
    exact: true,
    component: Leaderboard,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/settings-v2/:section?",
    exact: true,
    component: LegacySettingsV2Redirect,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/settings",
    component: Account,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/reports/:reportId",
    exact: true,
    component: ReportDetail,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/reports",
    exact: true,
    component: Reports,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/reports/reputation/adpass/:userId",
    exact: true,
    component: ReputationReview,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/reports/reputation",
    exact: true,
    component: ReputationReview,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/reputation",
    exact: true,
    component: TemplateReputationPage,
    loadData: () => () => Promise.resolve(),
  },
  // {
  //   path: "/tires",
  //   exact: true,
  //   component: Home,
  //   loadData: () => getHomeData(),
  // },
  // {
  //   path: "/tires-hot-deals",
  //   exact: true,
  //   component: Home,
  //   loadData: () => getHomeData(),
  // },
  // {
  //   path: "/tires-super-sale",
  //   exact: true,
  //   component: Home,
  //   loadData: () => getHomeData(),
  // },
  // {
  //   path: "/motorcycle-tires",
  //   exact: true,
  //   component: Home,
  //   loadData: () => getHomeData(),
  // },
  // {
  //   path: "/our-direct-business-model",
  //   exact: true,
  //   component: BusinessModelPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/list/moto/by/brand/:brand",
  //   exact: true,
  //   component: TiresResultsByBrandPage,
  //   loadData: (a) => getListByBrandPageData(a),
  // },
  // {
  //   path: "/list/moto/:width/:profile/:rim",
  //   exact: true,
  //   component: TireResultsPage,
  //   loadData: (a) => getListPageData(a),
  // },
  // {
  //   path: "/list/moto/category/:category",
  //   exact: true,
  //   component: CategoryMotoResultsPage,
  //   loadData: (a) => getListPageDataForCategory(a),
  // },
  // {
  //   path: "/tire/moto/:ean/:name",
  //   exact: true,
  //   component: ProductPage,
  //   loadData: (a) => getProductPageData(a),
  // },
  // {
  //   path: "/our-shops/:name",
  //   exact: true,
  //   component: ShopPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/our-shops",
  //   exact: true,
  //   component: OurShopsPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/coh",
  //   exact: true,
  //   component: AdminPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/cart",
  //   exact: true,
  //   component: CartPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/checkout",
  //   exact: true,
  //   component: CheckoutPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  {
    path: "/membership/plans",
    exact: true,
    component: PlansPage,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/membership/success",
    exact: true,
    component: MembershipSuccessPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/membership/cancel",
    exact: true,
    component: MembershipCancelPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/anti-cheat",
    exact: true,
    component: TemplateAntiCheatPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/how-it-works",
    exact: true,
    component: TemplateHowItWorksPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/trust-and-safety",
    exact: true,
    component: TemplateTrustAndSafetyPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/faq",
    exact: true,
    component: TemplateFaqPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/contact",
    exact: true,
    component: TemplateContactPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/pricing",
    exact: true,
    component: TemplatePricingPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/support",
    exact: true,
    component: TemplateSupportPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/privacy",
    exact: true,
    component: TemplatePrivacyPolicyPage,
    loadData: () => () => Promise.resolve(),
  },
  // {
  //   path: "/returns-and-refund-policy",
  //   exact: true,
  //   component: ReturnsAndRefundPolicy,
  //   loadData: () => () => Promise.resolve(),
  // },
  {
    path: "/about-us",
    exact: true,
    component: TemplateAboutUsPage,
    loadData: () => () => Promise.resolve(),
  },
  // {
  //   path: "/imprint",
  //   exact: true,
  //   component: ImprintPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/accessibility-policy",
  //   exact: true,
  //   component: AccessibilityPolicyPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  {
    path: "/data-protection-policy-and-gdpr",
    exact: true,
    component: TemplateGDPRPage,
    loadData: () => () => Promise.resolve(),
  },
  // {
  //   path: "/shop/signup",
  //   exact: true,
  //   component: ShopSignupPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  {
    path: "/terms-of-use",
    exact: true,
    component: TemplateTermsOfUsePage,
    loadData: () => () => Promise.resolve(),
  },
  // {
  //   path: "/testimonials",
  //   exact: true,
  //   component: TestimonialsPage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/customer-service",
  //   exact: true,
  //   component: CustomerServicePage,
  //   loadData: () => () => Promise.resolve(),
  // },
  // {
  //   path: "/contact-us",
  //   exact: true,
  //   component: CustomerServicePage,
  //   loadData: () => () => Promise.resolve(),
  // },
  {
    path: "/guard/connect",
    exact: true,
    component: GuardConnectPage,
    requiresAuth: true,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/auth/playsafe/status",
    exact: true,
    component: PlaySafeStatusPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/auth/steam/status",
    exact: true,
    component: PlaySafeStatusPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/auth/playsafe/connect-steam",
    exact: true,
    component: PlaySafeConnectSteamPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    path: "/match-night",
    exact: true,
    component: MatchNightPage,
    loadData: () => () => Promise.resolve(),
  },
  {
    component: NotFound,
  },
];

function AppWithRoutes({ children }) {
  const location = useLocation();
  return (
    <App key={isV2Path(location.pathname) ? "v2" : "legacy"}>
      {children}
    </App>
  );
}

export default function Router() {
  const location = useLocation();

  return (
    <App>
      <Switch location={location}>
        {routes.map((route) => {
          const RouteComponent = route.requiresAuth ? ProtectedRoute : Route;
          return <RouteComponent key={route.path || "notfound"} {...route} />;
        })}
      </Switch>
    </App>
  );
}
